import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Heart, Handshake, Shield, Sparkles, Target, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_SLIDESHOW = [
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/ehocqn8c__DSC9363.jpeg',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/wpwu5gu1_IMG_4619.jpg',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/w2uilbl3_IMG_4627.jpg',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/ziqpzk1s_IMG_1270.jpg',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/rwz6m6ax_5f5f0aed-9da8-43f7-852f-6a7a93c9c9b8.JPG',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/mkjk3d0j_8a07c135-6ef6-4fb9-a7cd-1c767d637a5a.JPG',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/82kj4426_IMG_5191.JPG',
  'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/wodbh26k_IMG_5193.JPG',
];

const IMAGES = {
  community: "https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/7qco3wb0_IMG_1602.jpeg",
};

const PARTNER_LOGOS = [
  { name: 'Piano Love', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/7ibe6ine_e3390554-1182-4073-8dbe-9d7e0f508a62.jpeg' },
  { name: 'TheStockroom', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/ahgvaoj9_TheStockroom%20Transparent%20Logo%20%282%29.png' },
  { name: 'Save a Life Give Blood', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/i2gek46q_save-a-life-give-blood-logo-png_seeklogo-303827.webp' },
  { name: 'Wingstop', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/l913l8nb_Wingstop-Logo-500x281.webp' },
  { name: 'Red Bull Basement', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/cvewfkzl_Basement%20logo.webp' },
  { name: 'Vita Coco', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/vxejf9sq_Vita_Coco_Logo-3000x2376.webp' },
  { name: 'Islington Council', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/kunmniq4_islington-logo-news-placeholder.webp' },
  { name: 'Lifelong Links', src: 'https://customer-assets.emergentagent.com/job_ovyc-london/artifacts/jssoog2l_LL_logo.webp' },
];

const services = [
  {
    icon: Users,
    title: 'Community Events',
    description: 'Culturally aware gatherings that celebrate diversity and bring people together in meaningful ways.',
  },
  {
    icon: Heart,
    title: 'Youth-Focused Projects',
    description: 'Creative programmes that empower young people with confidence, skills, and safe spaces to grow.',
  },
  {
    icon: Handshake,
    title: 'Brand Partnerships',
    description: 'Purpose-driven collaborations with organisations committed to real social impact in communities.',
  },
];

const impactStats = [
  { number: 6, suffix: '+', label: 'Sessions Delivered' },
  { number: 4, suffix: '', label: 'Partner Organisations' },
  { number: 400, suffix: 'k', label: 'Digital Reach' },
  { number: 500, suffix: '+', label: 'Community Members Reached' },
];

const differentiators = [
  {
    icon: Shield,
    title: 'Community-Led',
    description: 'Every project starts with listening. We design with communities, not for them.',
  },
  {
    icon: Sparkles,
    title: 'Culturally Aware',
    description: 'Our work respects and celebrates the diverse cultures that make London vibrant.',
  },
  {
    icon: Target,
    title: 'Impact-Focused',
    description: 'We measure what matters: connection, confidence, and lasting positive change.',
  },
];

// Counter animation hook
const useCounter = (target, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [target, duration, shouldStart]);
  
  return count;
};

const StatCard = ({ stat, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const count = useCounter(stat.number, 2000, isVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (ref.current) {
      observer.observe(ref.current);
    }
    
    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={ref}
      className="text-center"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="text-5xl md:text-6xl font-bold text-[#2D2A26] mb-2 font-['Manrope']">
        {count}{stat.suffix}
      </div>
      <div className="text-[#5C5852] text-sm md:text-base">{stat.label}</div>
    </div>
  );
};

export const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDESHOW.length);
    }, 4000); // Change slide every 4 seconds
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % HERO_SLIDESHOW.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + HERO_SLIDESHOW.length) % HERO_SLIDESHOW.length);
  };

  return (
    <main data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-20 bg-[#FAF9F6]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Hero Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E3B505]/10 rounded-full">
                <span className="w-2 h-2 bg-[#E3B505] rounded-full"></span>
                <span className="text-sm font-medium text-[#5C5852]">London-based Community Organisation</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D2A26] font-['Manrope']">
                Rebuilding community through{' '}
                <span className="text-[#E3B505]">safe spaces</span>, creative projects and{' '}
                <span className="text-[#E76F51]">meaningful partnerships</span>
              </h1>
              
              <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed max-w-xl">
                Our Village, Your Community creates and delivers community-led events, youth-focused 
                opportunities and brand-supported initiatives that bring people together and make 
                support feel real.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/partner">
                  <Button 
                    data-testid="hero-partner-btn"
                    className="w-full sm:w-auto bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-14 px-8 text-base font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    Partner With Us
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    data-testid="hero-learn-btn"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-[#264653] text-[#264653] hover:bg-[#264653]/10 rounded-full h-14 px-8 text-base font-medium"
                  >
                    Learn More About OVYC
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Hero Slideshow */}
            <div className="relative animate-fade-in-up delay-200">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                {/* Slideshow Container */}
                <div className="relative h-[400px] md:h-[500px]">
                  {HERO_SLIDESHOW.map((src, index) => (
                    <img 
                      key={index}
                      src={src}
                      alt={`OVYC community event ${index + 1}`}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        index === currentSlide ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#264653]/30 to-transparent"></div>
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  data-testid="slideshow-prev"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5 text-[#2D2A26]" />
                </button>
                <button
                  onClick={nextSlide}
                  data-testid="slideshow-next"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5 text-[#2D2A26]" />
                </button>
                
                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {HERO_SLIDESHOW.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      data-testid={`slideshow-dot-${index}`}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        index === currentSlide 
                          ? 'bg-[#E3B505] w-8' 
                          : 'bg-white/60 hover:bg-white'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
              {/* Floating accent */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#E3B505]/20 rounded-full blur-3xl"></div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#E76F51]/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              What is OVYC?
            </h2>
            <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed">
              OVYC, Our Village, Your Community, is a London-based community project management 
              organisation dedicated to rebuilding connection, creating safe spaces, and delivering 
              impactful programmes that support young people and families. We work alongside brands, 
              venues, and organisations to make community support feel accessible, meaningful, and real.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        data-testid="services-section"
        className="py-20 md:py-28 bg-[#F4F1DE]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">What We Do</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Our Core Services
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={service.title}
                data-testid={`service-card-${index}`}
                className="group bg-white rounded-3xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-[#E6E2D8]/50"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#E3B505]/10 flex items-center justify-center mb-6 group-hover:bg-[#E3B505] transition-colors">
                  <service.icon className="w-7 h-7 text-[#E3B505] group-hover:text-[#2D2A26] transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-3 font-['Manrope']">{service.title}</h3>
                <p className="text-[#5C5852] leading-relaxed">{service.description}</p>
                <Link 
                  to="/about" 
                  className="inline-flex items-center text-[#E3B505] font-medium mt-4 hover:gap-2 transition-all"
                >
                  Learn more <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why OVYC Exists */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img 
                src={IMAGES.community}
                alt="Community workshop in action"
                className="w-full h-[400px] md:h-[500px] object-cover rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
            </div>
            
            <div className="space-y-6">
              <span className="text-sm font-medium tracking-wide uppercase text-[#E76F51]">Our Purpose</span>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] font-['Manrope']">
                Why OVYC Exists
              </h2>
              <div className="space-y-4 text-[#5C5852] text-lg leading-relaxed">
                <p>
                  Too many communities have lost access to the safe spaces, youth programmes, and 
                  support networks they once relied on. Young people are growing up without the 
                  structured opportunities that help build confidence, connection, and belonging.
                </p>
                <p>
                  OVYC exists to bridge that gap. We create spaces where communities can come together, 
                  where young people can thrive, and where partnerships with brands and organisations 
                  translate into real, tangible support for the people who need it most.
                </p>
                <p>
                  We believe that community rebuilding is possible, and it starts with showing up, 
                  listening, and delivering projects that make people feel seen, supported, and part 
                  of something bigger.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section 
        data-testid="stats-section"
        className="py-20 md:py-28 bg-[#E3B505]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Our Impact So Far
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {impactStats.map((stat, index) => (
              <StatCard key={stat.label} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Partner Logos Strip */}
      <section 
        data-testid="partners-section"
        className="py-16 bg-white border-y border-[#E6E2D8]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-sm font-medium tracking-wide uppercase text-[#5C5852] mb-10">
            Organisations We've Worked With
          </p>
          <div className="flex items-center justify-center gap-8 md:gap-16 flex-wrap">
            {PARTNER_LOGOS.map((partner) => (
              <img 
                key={partner.name}
                src={partner.src}
                alt={partner.name}
                className="h-12 md:h-16 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
              />
            ))}
          </div>
        </div>
      </section>

      {/* What Makes OVYC Different */}
      <section className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#264653] mb-4 block">Our Approach</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              What Makes OVYC Different
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {differentiators.map((item, index) => (
              <div 
                key={item.title}
                data-testid={`differentiator-${index}`}
                className="text-center p-8"
              >
                <div className="w-16 h-16 rounded-full bg-[#264653] flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-3 font-['Manrope']">{item.title}</h3>
                <p className="text-[#5C5852] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        data-testid="cta-section"
        className="py-20 md:py-28 bg-[#264653]"
      >
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 font-['Manrope']">
            Ready to Make a Difference Together?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Whether you're a brand looking to create meaningful impact, an organisation seeking 
            community partnerships, or someone who wants to get involved, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/partner">
              <Button 
                data-testid="cta-partner-btn"
                className="bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-14 px-10 text-base font-medium shadow-lg"
              >
                Partner With OVYC
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                data-testid="cta-contact-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full h-14 px-10 text-base font-medium"
              >
                Get In Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
